/* ==========================================================================================
   CEO 2026 · customer.js · the classic customer account pages
   (sections/main-login, main-register, main-account, main-addresses, main-order,
   main-activate-account, main-reset-password)

   Dawn 15's customer.js, rewritten. Dawn's version bound EVERY button[aria-expanded] on the page (the
   header's menus too) and needed global.js for the country list. This one touches only the account
   sections, needs nothing else, is safe to load twice, and re-binds when the theme editor redraws a section.
   The pages work without it: the forms are plain Shopify forms. It adds:

     1. Show / Hide on password boxes          [data-ce-pw] aria-controls="<input id> [<input id>]", its word in
                                               [data-ce-pw-word], data-show / data-hide; .is-on while shown
     2. Sign in <-> reset password panes       [data-ce-acct-panes]  (#recover / #login in the address)
     3. Focus on a message that needs reading  [data-ce-acct-focus]
     4. Address book                           [data-customer-addresses]
          open / close a form                  [data-ce-acct-toggle] aria-controls -> [data-ce-acct-panel]
          cancel                               [data-ce-acct-cancel] (a reset button)
          delete, asked twice                  form[data-ce-confirm] > [data-ce-confirm-ask] / -box / -yes / -no
          country -> state list                select[data-ce-country] data-ce-province data-ce-province-box
                                               data-default; data-ce-options-from="<id of the full list>"
     5. Print an order                         [data-ce-acct-print]  (hidden in the markup; shown here, since
                                               printing needs JavaScript; the print look is in ce-account.css)

   window.CEAccount.init(root) binds everything inside root. (window.CustomerAddresses is kept as a
   harmless stand-in for any Dawn markup that still calls `new CustomerAddresses()`.)
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEAccount) return;

  const W = window, D = document, H = D.documentElement;
  H.classList.add('ce-acct-js');

  const closest = (el, sel) => (el && el.closest ? el.closest(sel) : null);

  /* ---------- 1. Show / Hide password ---------- */
  const pwInputs = (btn) => (btn.getAttribute('aria-controls') || '')
    .split(/\s+/)
    .map((id) => (id ? D.getElementById(id) : null))
    .filter(Boolean);
  // The button's words say what a press will do ("Show password" / "Hide password": the second word is
  // there for screen readers only), so it carries no aria-pressed: "Hide, pressed" would say it twice.
  const setPw = (btn, show) => {
    pwInputs(btn).forEach((input) => { input.type = show ? 'text' : 'password'; });
    btn.classList.toggle('is-on', show);
    (btn.querySelector('[data-ce-pw-word]') || btn).textContent = show ? (btn.dataset.hide || 'Hide') : (btn.dataset.show || 'Show');
  };

  /* ---------- 2. Sign in <-> reset password ----------
     CSS shows the right pane from the address alone (#recover). This adds: a reset form that came back
     with an error gives way when "Back to sign in" is pressed, and the first box of the new pane gets focus. */
  const onHash = () => {
    const root = D.querySelector('[data-ce-acct-panes]');
    if (!root) return;
    const hash = W.location.hash;
    if (hash !== '#recover' && hash !== '#login') return;
    root.classList.toggle('is-recover', hash === '#recover');
    const pane = D.getElementById(hash.slice(1));
    const first = pane && pane.querySelector('input:not([type="hidden"])');
    if (first) first.focus({ preventScroll: true });
  };

  /* ---------- 4. Address book ---------- */
  const pick = (select, want) => {                   // choose an option by its value, else by its words
    if (!want) return false;
    for (let i = 0; i < select.options.length; i++) {
      const o = select.options[i];
      if (o.value === want || o.textContent === want) { select.selectedIndex = i; return true; }
    }
    return false;
  };
  const borrowOptions = (select) => {                // an edit form borrows the full country list, once
    const from = select.dataset.ceOptionsFrom ? D.getElementById(select.dataset.ceOptionsFrom) : null;
    if (!from || from === select || select.dataset.ceFilled || !from.options.length) return;
    const frag = D.createDocumentFragment();
    for (let i = 0; i < from.options.length; i++) frag.appendChild(from.options[i].cloneNode(true));
    select.textContent = '';
    select.appendChild(frag);
    select.dataset.ceFilled = '1';
  };
  const fillProvinces = (select, keepDefault) => {   // the states of the chosen country
    const province = D.getElementById(select.dataset.ceProvince || '');
    if (!province) return;
    const box = D.getElementById(select.dataset.ceProvinceBox || '');
    const option = select.options[select.selectedIndex];
    let list = [];
    try { list = JSON.parse((option && option.getAttribute('data-provinces')) || '[]') || []; } catch (e) { list = []; }
    const want = keepDefault ? province.getAttribute('data-default') : '';
    province.textContent = '';
    list.forEach((pair) => {
      const o = D.createElement('option');
      o.value = pair[0];
      o.textContent = pair[1];
      province.appendChild(o);
    });
    // no saved state to go back to: start on "Choose a state" (and insist on a choice), never on the first of the list
    const found = want ? pick(province, want) : false;
    const ask = province.getAttribute('data-ce-placeholder');
    if (!found && ask && list.length) {
      const o = D.createElement('option');
      o.value = '';
      o.textContent = ask;
      o.disabled = true;
      province.insertBefore(o, province.firstChild);
      province.selectedIndex = 0;
    }
    province.required = !!(ask && list.length);
    if (box) box.hidden = !list.length;
  };
  const bindCountry = (select) => {
    if (select.dataset.ceBound) return;
    select.dataset.ceBound = '1';
    const sync = () => {
      pick(select, select.getAttribute('data-default'));
      fillProvinces(select, true);
    };
    borrowOptions(select);
    sync();
    select.addEventListener('change', () => fillProvinces(select, false));
    if (select.form) select.form.addEventListener('reset', () => setTimeout(sync, 0));   // Cancel puts the saved address back
  };

  const setOpen = (button, open, focus) => {
    const panel = D.getElementById(button.getAttribute('aria-controls') || '');
    if (!panel) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.classList.toggle('is-open', open);
    const box = closest(button, '[data-address]');
    if (box) box.classList.toggle('is-editing', open);
    if (!open) return;
    panel.querySelectorAll('select[data-ce-country]').forEach(bindCountry);
    if (focus) {                                     // the first box to TYPE in (the country above it is already chosen)
      const first = panel.querySelector('input:not([type="hidden"]):not([type="checkbox"])') || panel.querySelector('select');
      if (first) setTimeout(() => first.focus(), 60);
    }
  };
  const toggleOf = (panel) => (panel && panel.id ? D.querySelector('[data-ce-acct-toggle][aria-controls="' + panel.id + '"]') : null);

  const askDelete = (form, asking) => {
    const ask = form.querySelector('[data-ce-confirm-ask]');
    const box = form.querySelector('[data-ce-confirm-box]');
    if (!ask || !box) return false;
    ask.hidden = asking;
    box.hidden = !asking;
    const next = asking ? box.querySelector('[data-ce-confirm-no]') : ask;
    if (next) next.focus();
    return true;
  };

  /* ---------- one set of listeners for the whole page ---------- */
  D.addEventListener('pointerdown', (event) => {     // a mouse press on Show / Hide must not take the caret out of the box
    if (event.pointerType === 'mouse' && closest(event.target, '[data-ce-pw]')) event.preventDefault();
  });

  D.addEventListener('click', (event) => {
    const target = event.target;

    const pw = closest(target, '[data-ce-pw]');
    if (pw) { setPw(pw, !pw.classList.contains('is-on')); return; }

    if (closest(target, '[data-ce-acct-print]')) { W.print(); return; }

    const toggle = closest(target, '[data-ce-acct-toggle]');
    if (toggle) { setOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true', true); return; }

    const cancel = closest(target, '[data-ce-acct-cancel]');
    if (cancel) {                                    // a reset button: the browser empties the form, this closes it
      const button = toggleOf(closest(cancel, '[data-ce-acct-panel]'));
      if (button) { setOpen(button, false); button.focus(); }
      return;
    }

    const yes = closest(target, '[data-ce-confirm-yes]');
    if (yes && yes.form) {                           // let this one submit through; the flag does not outlive the press
      const form = yes.form;
      form.dataset.ceConfirmed = '1';
      setTimeout(() => { delete form.dataset.ceConfirmed; }, 1500);
      return;
    }

    const no = closest(target, '[data-ce-confirm-no]');
    if (no) { const form = closest(no, 'form[data-ce-confirm]'); if (form) askDelete(form, false); }
  });

  D.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || !form.matches) return;
    if (form.matches('form[data-ce-confirm]') && form.dataset.ceConfirmed !== '1') {
      if (askDelete(form, true)) event.preventDefault();   // first press: ask. "Yes" sets the flag and the form goes
      return;
    }
    // a password shown as text goes back to dots before the form leaves (browsers then offer to save it)
    form.querySelectorAll('[data-ce-pw].is-on').forEach((btn) => setPw(btn, false));
  }, true);

  D.addEventListener('keydown', (event) => {         // Escape backs out of the delete question
    if (event.key !== 'Escape') return;
    const box = closest(event.target, '[data-ce-confirm-box]');
    const form = box ? closest(box, 'form[data-ce-confirm]') : null;
    if (form) askDelete(form, false);
  });

  W.addEventListener('hashchange', onHash);

  /* ---------- bind what is on the page ---------- */
  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : D;
    scope.querySelectorAll('[data-ce-pw], [data-ce-acct-print]').forEach((btn) => { btn.hidden = false; });
    scope.querySelectorAll('[data-customer-addresses]').forEach((book) => {
      // the full country list (the new-address form) first, then any form that is already open
      book.querySelectorAll('select[data-ce-country]:not([data-ce-options-from])').forEach(bindCountry);
      book.querySelectorAll('[data-ce-acct-panel].is-open select[data-ce-country]').forEach(bindCountry);
    });
    const message = scope.querySelector('[data-ce-acct-focus]');
    if (message && (D.activeElement === D.body || !D.activeElement)) message.focus();
  };

  W.CEAccount = { init };
  W.CustomerAddresses = W.CustomerAddresses || function CustomerAddresses() { init(D); };

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D));
  else init(D);
  D.addEventListener('shopify:section:load', (event) => init(event.target));
})();
